// Stands in for <Auth0Provider> when the deployment runs transparent authentication.
//
// It feeds the SAME React context the Auth0 SDK uses, so every existing `useAuth0()` call site
// (user, isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect,
// logout) keeps working unchanged — backed by the session in src/auth/session.ts instead of a
// redirect to Auth0. The dev bypass uses the same technique with a mock (utils/authBypass).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth0Context } from '@auth0/auth0-react';
import { isLinkLanding, rememberAuthConfig } from './authMode';
import {
  decodeJwt,
  expiresAtOf,
  freshAccessToken,
  isSessionNotHere,
  leaveTo,
  readSessionProfile,
  readTokens,
  renewDelayMs,
  signOut,
  StoredTokens,
  useSessionTokens,
  useSessionVersion
} from './session';

interface Props {
  children: React.ReactNode;
  /**
   * Shown INSTEAD of the app when the browser's session belongs to an account this app's API does
   * not hold (the web app's other half shares the session). The app itself is not mounted then.
   */
  notSetUpHere?: React.ReactNode;
  /**
   * Shown while the page STARTS with a session whose access token ran out and is being renewed
   * (the app has not been on screen yet). Later renewals happen under the mounted app.
   */
  loading?: React.ReactNode;
}

/** The proactive renewal never fires more often than this (a token that is already due is renewed by the next request). */
const MIN_RENEW_INTERVAL_MS = 5000;
// Without Web Locks two tabs could renew at the same instant: spread them a little.
const jitterMs = (): number => ((navigator as any).locks?.request ? 0 : Math.floor(Math.random() * 3000));

const loginUrl = (): string => `${window.location.origin}${window.__APP_BASE__ || ''}/#/login`;

export function TransparentAuthProvider({ children, notSetUpHere, loading }: Props) {
  const tokens = useSessionTokens();
  // Profile arrived, or the session turned out not to be this app's.
  const version = useSessionVersion();
  const notHere = Boolean(tokens) && isSessionNotHere();

  // Re-evaluated when the access token's own expiry passes (a renewal that keeps failing changes
  // nothing in storage, and "signed in" must not outlive the token).
  const [, setExpiryTick] = useState(0);
  const expiresAt = tokens ? expiresAtOf(tokens) : 0;
  useEffect(() => {
    const remaining = expiresAt - Date.now();
    if (!tokens || remaining <= 0) return undefined;
    const timer = window.setTimeout(() => setExpiryTick((n) => n + 1), Math.min(remaining + 50, 2 ** 31 - 1));
    return () => window.clearTimeout(timer);
  }, [tokens, expiresAt]);

  // Signed in = holding an access token that is still good. A session whose token ran out but can
  // be renewed is "loading" until the renewal lands (it is retried on its own); the app's first
  // requests never go out with a dead token.
  const usable = Boolean(tokens) && !notHere && expiresAt > Date.now();
  const renewing = Boolean(tokens) && !notHere && !usable && Boolean(tokens?.refresh_token);
  // An e-mailed link does not need the previous session: it is not renewed for it (nor waited for).
  const onLink = isLinkLanding();
  useEffect(() => {
    if (renewing && !onLink) freshAccessToken();
  }, [renewing, onLink]);

  // Keep the session alive while the app is open: renew shortly before expiry, and re-check when
  // the tab becomes visible again (timers do not run while a laptop sleeps).
  useEffect(() => {
    const delay = renewDelayMs();
    if (delay === null) return undefined;
    // setTimeout overflows above ~24.8 days; an access token never lives that long, but stay safe.
    const timer = window.setTimeout(() => {
      freshAccessToken({ force: true });
    }, Math.min(Math.max(delay, MIN_RENEW_INTERVAL_MS) + jitterMs(), 2 ** 31 - 1));
    const onVisible = () => {
      if (document.visibilityState === 'visible') freshAccessToken();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tokens]);

  // Has the app been shown in this page load? Once it has, it stays mounted through a renewal
  // (unsaved work must not vanish); before that, a renewal that takes a while shows a loader
  // instead of an empty page.
  const shownRef = useRef(false);
  const waitingToStart = renewing && !onLink && !shownRef.current;
  if (!waitingToStart) shownRef.current = true;

  // useLogout() clears localStorage BEFORE calling logout(): keep what sign-out needs reachable.
  const sessionRef = useRef<StoredTokens | null>(null);
  sessionRef.current = tokens ?? sessionRef.current;

  const value = useMemo(() => {
    const claims = usable && tokens ? decodeJwt(tokens.id_token) : null;
    // Legacy app: `user_metadata` was a claim added by an Auth0 Action; the API now hands it over
    // next to the tokens (session profile) — and that is what counts, whatever a tenant Action
    // may still stamp on the token.
    const profile = usable ? readSessionProfile() : null;
    return {
      isAuthenticated: usable,
      isLoading: renewing && !onLink,
      error: undefined,
      user: claims
        ? {
            ...claims,
            ...(profile ? { user_metadata: { ...(claims.user_metadata ?? {}), ...profile.user_metadata } } : {})
          }
        : undefined,
      getAccessTokenSilently: async (options?: { ignoreCache?: boolean }) => {
        const token = await freshAccessToken({ force: options?.ignoreCache === true });
        if (!token) throw new Error('Login required');
        return token;
      },
      getAccessTokenWithPopup: async () => (await freshAccessToken()) ?? undefined,
      getIdTokenClaims: async () => {
        const current = readTokens();
        return current ? { ...decodeJwt(current.id_token), __raw: current.id_token } : undefined;
      },
      // There is nowhere to redirect to: "log in" means showing the app's own sign-in screen.
      loginWithRedirect: async () => {
        if (!window.location.hash.startsWith('#/login')) window.location.assign(loginUrl());
      },
      loginWithPopup: async () => {
        if (!window.location.hash.startsWith('#/login')) window.location.assign(loginUrl());
      },
      handleRedirectCallback: async () => ({ appState: undefined }),
      logout: (options?: { returnTo?: string }) => {
        signOut(sessionRef.current?.refresh_token, sessionRef.current?.region);
        sessionRef.current = null;
        // The caller cleared the whole storage: the next load must still know this deployment's mode.
        rememberAuthConfig();
        leaveTo(options?.returnTo || loginUrl());
      }
    };
    // `version`: the profile is read from storage, not from `tokens`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens, usable, renewing, onLink, version]);

  return (
    <Auth0Context.Provider value={value as any}>
      {notHere && notSetUpHere ? notSetUpHere : waitingToStart && loading ? loading : children}
    </Auth0Context.Provider>
  );
}

export default TransparentAuthProvider;
